import React, { useState, useEffect, useRef } from 'react';
import { StreamEmbed } from './StreamEmbed';
import { StreamChat } from './StreamChat';
import { useData } from '../backend/firebase';

const MultiStreamPlayer = ({ event }) => {
    const { teams } = useData();
    const [viewMode, setViewMode] = useState('grid'); 
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [mutedState, setMutedState] = useState({}); 
    const [volumeState, setVolumeState] = useState({}); 
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [chatState, setChatState] = useState({}); // { index: boolean } -> true means show chat

    const playerRef = useRef(null); // Reference to the main container

    const toggleFullScreen = async () => {
        if (!document.fullscreenElement) {
            try {
                await playerRef.current.requestFullscreen();
            } catch (err) {
                console.error("Error attempting to enable fullscreen:", err);
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
    };

    // Handle ESC key to exit fullscreen
    useEffect(() => {
        const handleChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleChange);
        return () => document.removeEventListener('fullscreenchange', handleChange);
    }, []);

    if (!event) return null;

    let activeLinks = [];
    if (event.links && event.links.length > 0) {
        activeLinks = event.links.map(l => (typeof l === 'string' ? {url: l, label: "", teamId: ""} : l));
    } else if (event.link) {
        activeLinks = [{url: event.link, label: "", teamId: ""}];
    }

    if (activeLinks.length === 0) return null;

    const toggleMute = (idx) => {
        setMutedState(prev => {
            const currentlyMuted = prev[idx] === undefined ? true : prev[idx];
            return { ...prev, [idx]: !currentlyMuted };
        });
        if ((mutedState[idx] === undefined || mutedState[idx] === true) && (volumeState[idx] || 0) === 0) {
             setVolumeState(prev => ({...prev, [idx]: 50}));
        }
    };

    const toggleChat = (idx) => {
        setChatState(prev => ({...prev, [idx]: !prev[idx]}));
    };

    const handleVolumeChange = (idx, val) => {
        setVolumeState(prev => ({...prev, [idx]: parseInt(val)}));
        if (val > 0) setMutedState(prev => ({...prev, [idx]: false}));
    };

    const renderTeamIcon = (teamId) => {
        const team = teams.find(t => t.id.toString() === teamId?.toString());
        if (!team) return null;
        if (team.logo) {
            const svgDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(team.logo)}`;
            return <img src={svgDataUrl} alt={team.name} style={{width: 20, height: 20, objectFit: 'contain'}} />;
        }
        return <div style={{width: 16, height: 16, borderRadius: '50%', background: team.color}}></div>;
    };

    // --- RENDER CONTENT FOR A SINGLE CELL ---
    const renderCell = (linkObj, idx) => {
        const isMuted = mutedState[idx] === undefined ? true : mutedState[idx];
        const vol = volumeState[idx] || 0;
        const showChat = chatState[idx] || false;

        return (
            <div key={idx} style={{display: 'flex', flexDirection: 'column', background: '#000', height: '100%', width: '100%', position: 'relative'}}>
                
                {/* VIDEO CONTAINER */}
                <div style={{flex: 1, position: 'relative', overflow: 'hidden'}}>
                    
                    {/* The Video (Always Rendered) */}
                    <div style={{width: '100%', height: '100%'}}>
                        <StreamEmbed link={linkObj.url} autoplay={true} muted={isMuted} volume={vol} />
                    </div>

                    {/* Chat Overlay (Conditional) */}
                    {showChat && (
                        <div style={{
                            position: 'absolute', 
                            bottom: 10, right: 10, 
                            width: '40%', height: '60%', minWidth: 250, minHeight: 300,
                            background: 'rgba(0,0,0,0.85)', 
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 8, overflow: 'hidden',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.5)',
                            zIndex: 10,
                            display: 'flex', flexDirection: 'column'
                        }}>
                             <div style={{
                                 padding: '5px 10px', background: '#1a1a2e', fontSize: 10, fontWeight: 'bold', 
                                 display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #333'
                             }}>
                                 <span>STREAM CHAT</span>
                                 <button onClick={() => toggleChat(idx)} style={{background: 'transparent', border: 'none', color: '#FF5C5C', cursor: 'pointer', fontSize: 14}}>×</button>
                             </div>
                             <div style={{flex: 1}}>
                                <StreamChat link={linkObj.url} />
                             </div>
                        </div>
                    )}
                </div>
                
                {/* CONTROLS BAR */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', background: '#1a1a2e', borderTop: '1px solid #333',
                    flexShrink: 0
                }}>
                    <div style={{flex: 1, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0}}>
                        {renderTeamIcon(linkObj.teamId)}
                        <span style={{fontSize: 11, fontWeight: 'bold', color: '#ccc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                            {linkObj.label || `View ${idx+1}`}
                        </span>
                    </div>

                    <button 
                        onClick={() => toggleChat(idx)}
                        style={{
                            background: showChat ? '#6C5DD3' : 'transparent',
                            border: '1px solid #6C5DD3', color: 'white',
                            borderRadius: 4, padding: '2px 8px', fontSize: 10, cursor: 'pointer', fontWeight: 'bold'
                        }}
                    >
                        💬 Chat
                    </button>

                    <a 
                        href={linkObj.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        title="Open Channel"
                        style={{textDecoration: 'none'}}
                    >
                        <button style={{
                            background: '#FF5C5C', border: 'none', color: 'white',
                            borderRadius: 4, padding: '2px 8px', fontSize: 10, cursor: 'pointer', fontWeight: 'bold'
                        }}>
                            ❤️ Follow
                        </button>
                    </a>

                    {/* Volume Controls (Now active for everyone) */}
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, borderLeft: '1px solid #444', paddingLeft: 8}}>
                        <input 
                            type="range" min="0" max="100" step="5" 
                            value={isMuted ? 0 : vol} 
                            onChange={(e) => handleVolumeChange(idx, e.target.value)}
                            style={{width: 50, cursor: 'pointer', accentColor: '#6C5DD3'}}
                        />
                        <button 
                            onClick={() => toggleMute(idx)}
                            style={{background: 'transparent', border: 'none', color: isMuted ? '#FF5C5C' : 'white', cursor: 'pointer', fontSize: 14, padding: 0}}
                            title={isMuted ? "Unmute" : "Mute"}
                        >
                            {isMuted ? "🔇" : "🔊"}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // --- MAIN RENDER ---
    return (
        <div 
            ref={playerRef} // <--- Attach Ref Here
            style={{
                display: 'flex', 
                flexDirection: 'column', 
                width: '100%',
                // If native fullscreen is active, ensure background is black and height fills screen
                height: isFullScreen ? '100vh' : 'auto', 
                background: isFullScreen ? 'black' : 'transparent',
            }}
        >
            
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                padding: '10px 15px', background: '#151823', borderBottom: '1px solid #2D2B3F',
                flexShrink: 0
            }}>
                <div style={{fontWeight: 'bold', color: 'white', display:'flex', alignItems:'center', gap: 10, fontSize: 14}}>
                    {event.result && <span style={{background:'#FFD700', color:'black', fontSize:10, padding:'2px 6px', borderRadius:4}}>FINAL</span>}
                    <span style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: isFullScreen ? 600 : 200}}>
                        {event.title}
                    </span>
                </div>
                
                <div style={{display: 'flex', gap: 10, alignItems: 'center'}}>
                    {activeLinks.length > 1 && (
                        <div style={{display: 'flex', background: '#0C0A1A', borderRadius: 8, padding: 2}}>
                            <button onClick={() => setViewMode('single')} style={{background: viewMode === 'single' ? '#6C5DD3' : 'transparent', color: viewMode === 'single' ? 'white' : '#8F9EB5', border: 'none', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 'bold'}}>Single</button>
                            <button onClick={() => setViewMode('grid')} style={{background: viewMode === 'grid' ? '#6C5DD3' : 'transparent', color: viewMode === 'grid' ? 'white' : '#8F9EB5', border: 'none', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 'bold'}}>Grid</button>
                        </div>
                    )}
                    <button 
                        onClick={toggleFullScreen} // <--- Use the new helper function
                        style={{
                            background: 'transparent', 
                            border: '1px solid #444', 
                            color: '#ccc', 
                            padding: '4px 10px', 
                            borderRadius: 6, 
                            cursor: 'pointer', 
                            fontSize: 11
                        }}
                    >
                        {isFullScreen ? "Exit Fullscreen" : "⛶ Fullscreen"}
                    </button>
                </div>
            </div>

            <div style={{flex: 1, background: 'black', position: 'relative', overflow: 'hidden', display: 'flex'}}>
                {viewMode === 'single' && (
                    <div style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                        <div style={{flex: 1, position: 'relative'}}>
                             {renderCell(activeLinks[selectedIndex], selectedIndex)}
                        </div>
                        {activeLinks.length > 1 && (
                            <div style={{padding: 10, background: '#111', display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', flexShrink: 0}}>
                                {activeLinks.map((linkObj, idx) => {
                                    const isSel = idx === selectedIndex;
                                    return (
                                        <button key={idx} onClick={() => setSelectedIndex(idx)} style={{
                                            background: isSel ? '#6C5DD3' : '#333', color: 'white', border: 'none',
                                            padding: '5px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5
                                        }}>
                                            {renderTeamIcon(linkObj.teamId)}
                                            {linkObj.label || `View ${idx+1}`}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {viewMode === 'grid' && (
                    <div style={{
                        display: 'grid', 
                        gridTemplateColumns: activeLinks.length > 1 ? '1fr 1fr' : '1fr', 
                        gridTemplateRows: activeLinks.length > 2 ? '1fr 1fr' : '1fr',
                        gap: 2, 
                        width: '100%', height: '100%'
                    }}>
                        {activeLinks.map((linkObj, idx) => renderCell(linkObj, idx))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MultiStreamPlayer;